import { useState } from 'react';
import toast from 'react-hot-toast';
import { ImportResult, ContactInput } from '../types';
import { apiService } from '../services/api';
import { validatePhone } from '../utils/phoneUtils';
import { ImportCategorySelect } from './ImportCategorySelect';

interface PasteImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function PasteImportModal({ isOpen, onClose, onSuccess }: PasteImportModalProps) {
  const [pastedText, setPastedText] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [categoriaId, setCategoriaId] = useState('');

  if (!isOpen) return null;

  const parsePastedContacts = (text: string): ContactInput[] => {
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    const contacts: ContactInput[] = [];
    let autoNameCounter = 0;
    const nextAutoName = () => {
      autoNameCounter++;
      return autoNameCounter === 1 ? 'Contato' : `Contato ${autoNameCounter}`;
    };

    const isHeaderLine = (line: string) => {
      const lower = line.toLowerCase();
      return lower.includes('nome') && lower.includes('telefone');
    };

    const isPhoneToken = (token: string) => {
      if (!token) return false;
      const digits = token.replace(/\D/g, '');
      // Mínimo 10 dígitos para telefones BR, permite com/sem +55
      return digits.length >= 10;
    };

    for (const line of lines) {
      if (isHeaderLine(line)) continue;
      const parts = line.split(/[,;|\t]/).map((p) => p.trim()).filter(Boolean);

      if (parts.length === 1) {
        const only = parts[0];
        if (isPhoneToken(only)) {
          const telefone = only;
          const nome = nextAutoName();
          contacts.push({ nome, telefone });
        }
        continue;
      }

      // Detectar qual coluna é telefone e qual é nome
      let nome: string | undefined;
      let telefone: string | undefined;
      let email: string | undefined;
      let observacoes: string | undefined;
      let categoriaId: string | undefined;

      const [c1, c2, c3, c4, c5] = parts;
      if (isPhoneToken(c1)) {
        telefone = c1;
        nome = c2;
        email = c3;
        observacoes = c4;
        categoriaId = c5;
      } else if (isPhoneToken(c2)) {
        nome = c1;
        telefone = c2;
        email = c3;
        observacoes = c4;
        categoriaId = c5;
      } else {
        // fallback padrão: primeira coluna nome, segunda telefone
        nome = c1;
        telefone = c2;
        email = c3;
        observacoes = c4;
        categoriaId = c5;
      }

      if (telefone) {
        contacts.push({ nome: nome || nextAutoName(), telefone, email, observacoes, categoriaId });
      }
    }
    return contacts;
  };

  const handleImport = async () => {
    if (!pastedText.trim()) {
      toast.error('Cole os contatos no campo de texto');
      return;
    }

    setIsUploading(true);
    try {
      const contacts = parsePastedContacts(pastedText);

      if (contacts.length === 0) {
        toast.error('Nenhuma linha válida encontrada. Formato mínimo: nome;telefone');
        setIsUploading(false);
        return;
      }

      let successfulImports = 0;
      let failedImports = 0;
      const errors: string[] = [];

      for (let i = 0; i < contacts.length; i++) {
        const c = contacts[i];
        try {
          if (!validatePhone(c.telefone)) {
            throw new Error(`Telefone inválido na linha ${i + 1} (${c.telefone})`);
          }
          await apiService.createContact({ ...c, categoriaId: c.categoriaId || categoriaId || undefined });
          successfulImports++;
        } catch (e) {
          const msg = e instanceof Error ? e.message : '';
          // Se backend indisponível, abortar o restante para evitar falhas em cascata
          if (msg && (msg.includes('Failed to fetch') || msg.includes('ECONNREFUSED'))) {
            const remaining = contacts.length - i;
            failedImports += remaining;
            errors.push('Servidor indisponível. Verifique o backend e tente novamente.');
            break;
          }
          failedImports++;
          errors.push(e instanceof Error ? e.message : `Erro desconhecido na linha ${i + 1}`);
        }
      }

      const result: ImportResult = {
        success: failedImports === 0,
        totalRows: contacts.length,
        successfulImports,
        failedImports,
        errors,
        message: failedImports === 0 ? 'Todos os contatos importados com sucesso' : 'Alguns contatos falharam',
      };
      setImportResult(result);

      if (result.success) {
        toast.success(`Importação concluída! ${result.successfulImports} contatos importados.`);
        onSuccess();
      } else {
        toast(`Importação parcial: ${result.successfulImports} sucessos, ${result.failedImports} erros.`, {
          icon: '⚠️',
          style: { border: '1px solid #f59e0b', color: '#92400e' },
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao importar contatos colados';
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setPastedText('');
    setImportResult(null);
    setCategoriaId('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl border border-gray-100 my-8" role="dialog" aria-labelledby="paste-import-title">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 id="paste-import-title" className="text-xl font-bold text-gray-900">
                Colar Contatos
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Cole os dados dos contatos e importe rapidamente
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Fechar"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="paste-area" className="block text-sm font-medium text-gray-700 mb-2">
                Colar contatos (um por linha)
              </label>
              <textarea
                id="paste-area"
                rows={8}
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
                placeholder={`Nome;Telefone;Email;Observacoes;CategoriaId\nJoão da Silva;5511999999999;joao@email.com;VIP;cat123\n5511999999999`}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <p className="text-xs text-gray-500 mt-2">
                Aceita: nome;telefone, telefone;nome, ou apenas telefone. Separadores: ';', ',' ou tab.
                Se o nome for omitido, será gerado automaticamente (Contato, Contato 2...).
              </p>
            </div>

            <ImportCategorySelect value={categoriaId} onChange={setCategoriaId} />

            {importResult && (
              <div className={`border rounded-lg p-4 ${importResult.success ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                <h3 className={`font-medium text-sm mb-3 ${importResult.success ? 'text-green-900' : 'text-yellow-900'}`}>
                  {importResult.success ? '✅' : '⚠️'} Resultado da Importação
                </h3>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-white rounded p-2 text-center">
                    <div className="text-lg font-bold text-gray-900">{importResult.totalRows}</div>
                    <div className="text-xs text-gray-600">Total</div>
                  </div>
                  <div className="bg-white rounded p-2 text-center">
                    <div className="text-lg font-bold text-green-600">{importResult.successfulImports}</div>
                    <div className="text-xs text-gray-600">Sucessos</div>
                  </div>
                  <div className="bg-white rounded p-2 text-center">
                    <div className="text-lg font-bold text-red-600">{importResult.failedImports}</div>
                    <div className="text-xs text-gray-600">Erros</div>
                  </div>
                </div>

                {importResult.errors.length > 0 && (
                  <details className="cursor-pointer">
                    <summary className="text-xs font-medium text-red-700 hover:text-red-800">Ver erros ({importResult.errors.length})</summary>
                    <div className="mt-2 bg-red-50 border border-red-200 rounded p-2 max-h-32 overflow-y-auto">
                      {importResult.errors.map((error, index) => (
                        <div key={index} className="text-xs text-red-600 py-1">{error}</div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleClose}
              className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 font-medium transition-colors text-sm"
            >
              Fechar
            </button>
            <button
              onClick={handleImport}
              disabled={isUploading || !pastedText.trim()}
              className="btn-primary flex-1 py-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
            >
              {isUploading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Importando...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  Importar
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}